/**
 * Phosphor palette.
 *
 * Two constraints meet here. Dwarf Fortress reads because it uses a small,
 * fixed, high-contrast terminal palette — a glyph's colour IS its identity.
 * Geometry Wars reads because every emitter is far brighter than white, so the
 * bloom skirt carries the colour outward instead of washing to grey.
 *
 * So: sixteen DF-shaped slots, hue-shifted into a CRT phosphor gamut, each
 * carrying an intensity multiplier above 1.0. Sprites sample the base colour;
 * the renderer multiplies by intensity before writing to the HDR buffer. Never
 * hand a raw sRGB triplet to an emissive material — go through `emissive()`.
 */

export type Rgb = readonly [number, number, number];

/** Linear-space base colours. Index order mirrors the classic 16-colour VGA set. */
export const PHOSPHOR = {
  black: [0.008, 0.010, 0.016],
  navy: [0.055, 0.090, 0.290],
  green: [0.075, 0.760, 0.240],
  teal: [0.070, 0.640, 0.700],
  maroon: [0.520, 0.075, 0.130],
  purple: [0.480, 0.110, 0.720],
  olive: [0.560, 0.400, 0.080],
  silver: [0.640, 0.680, 0.740],
  gray: [0.230, 0.260, 0.310],
  blue: [0.180, 0.420, 1.000],
  lime: [0.380, 1.000, 0.220],
  cyan: [0.150, 0.940, 1.000],
  red: [1.000, 0.180, 0.210],
  magenta: [1.000, 0.160, 0.780],
  yellow: [1.000, 0.870, 0.180],
  white: [0.960, 0.980, 1.000],
} as const satisfies Record<string, Rgb>;

export type PhosphorName = keyof typeof PHOSPHOR;

/**
 * How hard each slot drives the HDR buffer. Values above 1 are what produce a
 * visible bloom skirt; the dark slots stay at or below 1 so the background
 * lattice never blooms into mush.
 *
 * Ceiling on the bright slots: saturation survives only while the *weakest*
 * channel stays low in linear terms. Yellow's blue is 0.18, cyan's red is 0.15;
 * at the old 3.8-3.9 those "small" channels landed near 0.70 linear, which the
 * tonemap shoulder lifted to ~87% output. Keeping `intensity x min_channel`
 * under ~0.45 is what holds the hue. `white` sits *above* the coloured slots on
 * purpose, so explosion cores and tower fire out-rank them instead of tying,
 * which is where Geometry Wars gets its brightness hierarchy.
 */
export const INTENSITY: Record<PhosphorName, number> = {
  black: 0.0,
  navy: 0.35,
  green: 2.2,
  teal: 1.9,
  // Nothing reads `maroon`. It is raised to tower rank here so the one slot in
  // the palette that no enemy family and no playfield element claims is usable
  // as a tower fill — see the note on `towerRim` for why that matters and why
  // there is exactly one such slot. At 1.1 it sat below the corridor and a
  // tower wearing it would have read as a stain on the ground.
  maroon: 2.0,
  purple: 2.0,
  olive: 1.2,
  silver: 1.0,
  gray: 0.55,
  blue: 1.9,
  lime: 2.1,
  cyan: 2.2,
  red: 2.0,
  magenta: 2.1,
  yellow: 2.2,
  white: 2.6,
};

/** Base colour scaled into HDR. This is what emissive materials want. */
export function emissive(name: PhosphorName, gain = 1): Rgb {
  const c = PHOSPHOR[name];
  const i = INTENSITY[name] * gain;
  return [c[0] * i, c[1] * i, c[2] * i];
}

/** Packs a phosphor slot into a single 32-bit RGBA value for instance buffers. */
export function packed(name: PhosphorName): number {
  const c = PHOSPHOR[name];
  const r = Math.round(Math.min(1, c[0]) * 255);
  const g = Math.round(Math.min(1, c[1]) * 255);
  const b = Math.round(Math.min(1, c[2]) * 255);
  return (r << 24) | (g << 16) | (b << 8) | 0xff;
}

/** `#rrggbb` for DOM/UI use. Applies a rough linear→sRGB transfer. */
export function hex(name: PhosphorName): string {
  const c = PHOSPHOR[name];
  const enc = (v: number) => {
    const s = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
    return Math.round(Math.max(0, Math.min(1, s)) * 255).toString(16).padStart(2, '0');
  };
  return `#${enc(c[0])}${enc(c[1])}${enc(c[2])}`;
}

/**
 * Semantic assignments. Subsystems reference these, never a raw slot, so a
 * palette revision propagates everywhere in one edit.
 */
export const ROLE = {
  // Playfield
  background: 'black',
  // The lattice is a light source. `navy` peaks at 0.29 and could not clear the
  // bloom threshold at any sane intensity; `blue` can. `gridStressed` moves up
  // to `cyan` to keep a hue gap above it, otherwise the stress ramp in
  // WarpGrid's shader mixes blue into blue and does nothing.
  grid: 'blue',
  gridStressed: 'cyan',
  // Where enemies walk has to out-rank the wallpaper, the way BTD6's stone
  // out-ranks its grass. At the old teal-edge-on-a-1.35-grid the track edge
  // measured 1.027x the grid line — the most important read in a tower defense,
  // absent. Dropping the lattice to 0.80 does most of the work on its own:
  // `teal` (peak channel 0.70 x INTENSITY 1.9 x the edge gain) now lands 2.2x
  // the grid line. `cyan` was tried and overshoots badly — at 2.2 intensity the
  // two rails bloom hard enough that their skirts meet across a 39px corridor
  // and the whole tube blows out to white.
  //
  // The fill carries the *area* read, and it is the one place the playfield
  // wants a warm hue: BTD6 separates stone from grass by hue as much as value,
  // and `olive` is the only slot far enough from the blue lattice to do that
  // without colliding with an enemy family. It sits at 0.336 linear, well under
  // the bloom threshold, so it tints rather than glows.
  track: 'olive',
  trackEdge: 'teal',
  spawnPortal: 'magenta',
  exitPortal: 'red',

  // Enemies, by family
  grunt: 'lime',
  runner: 'cyan',
  armoured: 'silver',
  swarmling: 'yellow',
  weaver: 'magenta',
  splitter: 'green',
  shielded: 'blue',
  boss: 'red',

  // Towers
  //
  // Ownership is not carried by hue, and it cannot be. Sixteen slots: the eight
  // families above hold `lime cyan silver yellow magenta green blue red`, and
  // the playfield holds `black blue cyan olive teal magenta red`. Measured in
  // CIE LCh, *not one* of the five slots nobody has claimed is more than 16
  // degrees of hue from an enemy family — `navy` 12.4 from shielded, `maroon`
  // 9.7 from boss, `purple` 15.3 from weaver, `gray` 2.2 and `white` 9.7 from
  // armoured. There is no free hue band to move ten towers into. Moving the
  // *families* instead would cost more, since enemies outnumber towers in every
  // frame, and widening the palette past sixteen gives up the thing at the top
  // of this file that makes any of it read.
  //
  // So the binary a player actually asks — is that mine or theirs — is answered
  // by rim polarity, which no family uses, and *which* tower is answered by
  // silhouette, which `assets/towerSprites.ts` already varies across three
  // layers and five states each. Hue is then free to be decoration on both
  // sides, which is the only thing this palette has room for it to be.
  //
  // `white` is the rim because it is achromatic — it cannot collide in hue with
  // a family because it has no hue to collide with — and because it already
  // means *the player's own output* here: firing, explosions, damage numbers.
  // `silver` sits near it in value, but `silver` is a fill and never a rim, so
  // a bright edge on a coloured mass stays unique to a tower.
  //
  // That edge is no longer the whole ring. Ringing every tower in near-white
  // drew each one as a bright wireframe of itself — the rim ran brighter than
  // the fill it enclosed, so the eye read the cage and not the machine, and a
  // creature's dark outline looked like the solid object of the two. So
  // `render/TowerRenderer.ts` now wears the near-black outline on the shadowed
  // side, the way a creature does, and spends this slot only on the texels a
  // light above the board reaches. Measured on a ten-tower frame that leaves
  // the mark on roughly a third of each silhouette instead of all of it, which
  // is a real cost and is stated with the numbers in `tools/ownership.py`: what
  // it buys is that no creature can produce a bright achromatic edge at all, so
  // a third of a ring is still a mark nothing else on the board owns.
  towerRim: 'white',
  towerIdle: 'teal',
  towerReady: 'cyan',
  towerFiring: 'white',
  towerRange: 'teal',
  towerBlocked: 'red',

  // Combat
  projectile: 'yellow',
  explosion: 'white',
  damageNumber: 'white',
  critNumber: 'yellow',

  // UI chrome
  uiText: 'silver',
  uiTextBright: 'white',
  uiAccent: 'cyan',
  uiCash: 'yellow',
  uiLives: 'red',
  uiWave: 'lime',
  uiDanger: 'red',
  uiDisabled: 'gray',
} as const satisfies Record<string, PhosphorName>;

export type Role = keyof typeof ROLE;

export const roleHex = (r: Role): string => hex(ROLE[r]);
export const roleEmissive = (r: Role, gain = 1): Rgb => emissive(ROLE[r], gain);
